/*
 * Copyright © 2017-Present, The Freshwater Trust, all rights reserved.
 */

import {useMemo} from "react";
import * as formatters from "../Models/Extensions/Core/Formatters";

const getFormatter = (formatterName, formatterParams) => {
    const formatterClass = formatters[formatterName ?? 'NumberFormatter'];
    if(!formatterClass) {
        throw "Invalid Formatter Class";
    }
    return new formatterClass(formatterParams ?? []);
}

export const useFormatter = (formatterName, formatterParams = []) => {
    return useMemo(
        () => getFormatter(formatterName, formatterParams),
        [formatterName, formatterParams]
    );
}

export const useFormatValue = (formatterName, formatterParams) => {
    const formatter = useFormatter(formatterName, formatterParams);
    return (value) => formatter?.format?.(value) ?? value
}

// this is a custom hook that formats a number
// based on the code used in the UnitNumberFieldEditor
// it might be duplicative now, but I don't want to
// break anything by removing it or trying to refactor it
// TODO: consider if we still need this given the other
// formatter hooks above.
export const useFormattedValue = (number, options) => {
    
    const {
        maximumFractionDigits = 6,
        minimumFractionDigits = 0,
        canBeNegative,
        customFormatter = null
    } = options;
    
    return useMemo(() => {
        
        if (number === "") {
            return number;
        }
        
        // this prevents the default formatter from stripping 0
        // characters from the fraction as the user is typing
        // WARNING: if you use 'maximumSignificantDigits', it may cause
        // additional, unexpected decimal places to appear
        const formatterOptions = {
            maximumFractionDigits,
            minimumFractionDigits: Math.min(
                (number?.split?.('.')?.[1]?.length) ?? minimumFractionDigits,
                maximumFractionDigits
            ),
        };
        
        const formatter = customFormatter ? customFormatter : Intl.NumberFormat(undefined, formatterOptions);
        
        if (!canBeNegative && number < 0) {
            number = Math.abs(number);
        }
        
        let formattedNumber = number != null ? formatter.format(number) : "";
        
        // this prevents the default formatter from wiping
        // out the input if it doesn't recognize a number
        if (formattedNumber == "NaN") {
            return number;
        }
        
        // this prevents the default formatter from stripping .
        // characters from the fraction as the user is typing
        if (typeof number == 'string' && number.endsWith('.') && maximumFractionDigits > 0) {
            formattedNumber += '.';
        }
        
        return formattedNumber;
        
    }, [number, maximumFractionDigits, minimumFractionDigits, canBeNegative]);
}
